/**
 * Finance V2 — Portfolio Contribution (net portfolio profit under a chosen structure).
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../../lib/errors";
import { Section, Kpi, AsOfPicker, CategoryChip, fmtMoney0, fmtPct, fmtNum, todayIso, EmptyRealizedNotice, NotConfiguredNotice } from "./_shared";

export default function FinanceContribution() {
  const [asOf, setAsOf] = useState(todayIso());
  const [structures, setStructures] = useState([]);
  const [structId, setStructId] = useState("");
  const [mode, setMode] = useState("modeled");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadStructures = async () => {
    try {
      const { data } = await api.get("/finance/v2/capital-structures");
      setStructures(data.structures || []);
      const current = (data.structures || []).find(s => s.is_current);
      if (current) setStructId(current.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadData = async () => {
    setLoading(true);
    try {
      const params = { as_of: asOf, mode };
      if (structId) params.capital_structure_id = structId;
      const { data } = await api.get("/finance/v2/portfolio-contribution", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadStructures(); }, []);
  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [asOf, structId, mode]);

  const struct = data?.structure || {};
  const counts = data?.counts || {};
  const dep = data?.deployment || {};
  const rlz = data?.realized || {};
  const coc = data?.cost_of_capital || {};
  const con = data?.contribution || {};

  return (
    <div className="p-6 max-w-7xl" data-testid="finance-contribution-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0A2540]">Portfolio Contribution</h1>
          <div className="text-xs text-[#6B7280] mt-1">
            Gross realized funding revenue − attributable cost of capital − principal loss = Net Portfolio Contribution
          </div>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">Mode</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}
                    className="h-9 px-3 text-sm border border-[#E5E7EB] rounded-md bg-white" data-testid="mode-select">
              <option value="modeled">Modeled · uses selected structure</option>
              <option value="actual">Actual · snapshots only</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">Capital structure</label>
            <select value={structId} onChange={(e) => setStructId(e.target.value)}
                    className="h-9 px-3 text-sm border border-[#E5E7EB] rounded-md bg-white" data-testid="structure-select">
              <option value="">— Select a Capital Structure —</option>
              {structures.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {s.provenance === "actual" ? "Actual" : "Modeled"} · {s.annual_cost_of_capital_pct}%
                </option>
              ))}
            </select>
          </div>
          <AsOfPicker value={asOf} onChange={setAsOf} />
        </div>
      </div>

      {loading && <div className="text-sm text-[#6B7280]">Loading…</div>}
      {data && (
        <>
          {(!struct?.id || coc?.status === "not_configured") && <NotConfiguredNotice />}
          <Section title="Capital structure in effect" category={struct?.provenance === "actual" ? "contractual" : struct?.id ? "forecast" : "not_configured"}
                    subtitle={struct?.id
                       ? `${struct.name} — ${struct.annual_cost_of_capital_pct}% annual · Facility ${fmtMoney0(struct.facility_amount)}`
                       : "No Capital Structure configured. Cost-of-capital and contribution metrics are unavailable."}>
            <div className="text-xs text-[#4B5563]">
              {!struct?.id ? "Finance V2 does not assume a default cost of capital. Configure an Actual or Modeled structure to compute Net Portfolio Contribution."
                : struct.provenance === "actual"
                  ? "Cost-of-capital charges below are Contractual — sourced from signed terms."
                  : "Cost-of-capital charges below are Forecast — derived from a Modeled/Assumed capital structure. Configure an Actual structure to promote these values to Contractual."}
            </div>
          </Section>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <Kpi label="Loans in scope" value={fmtNum(counts.loans)} sub={`${counts.open} open · ${counts.closed} closed`} testid="kpi-count" />
            <Kpi label="Simple realized cohort" value={fmtNum(counts.simple_realized)} sub={`${counts.unresolved_realized} complex / missing-amt`} testid="kpi-simple" />
            <Kpi label="Cumulative capital deployed" value={fmtMoney0(dep.total_deployed_cumulative)} testid="kpi-cum-deployed" />
          </div>

          <Section title="Contractual position" category="contractual">
            <div className="grid grid-cols-4 gap-3">
              <Kpi label="Outstanding principal (open)" value={fmtMoney0(dep.outstanding_principal_open)} />
              <Kpi label="Contractual receivable (open)" value={fmtMoney0(dep.contractual_receivable_open)} />
              <Kpi label="Avg original advance" value={fmtMoney0(dep.avg_original_advance)} />
              <Kpi label="Avg total deployed" value={fmtMoney0(dep.avg_total_deployed)} />
            </div>
          </Section>

          {counts.simple_realized === 0 && <EmptyRealizedNotice />}

          <Section title="Realized economics (simple cohort)" category="realized">
            <div className="grid grid-cols-4 gap-3">
              <Kpi label="Recorded settlement total" value={fmtMoney0(rlz.recorded_settlement_total)} />
              <Kpi label="Principal returned" value={fmtMoney0(rlz.principal_returned)} />
              <Kpi label="Gross realized funding revenue" value={fmtMoney0(rlz.gross_realized_funding_revenue)} accent />
              <Kpi label="Principal loss" value={fmtMoney0(rlz.principal_loss)} negative />
              <Kpi label="Negotiated reductions (separate)" value={fmtMoney0(rlz.negotiated_reductions_total)} sub="Not subtracted from revenue" />
              <Kpi label="Avg case duration (days)" value={fmtNum(rlz.avg_case_duration_days)} />
              <Kpi label="Gross MOIC" value={rlz.gross_moic_simple_cohort ? rlz.gross_moic_simple_cohort.toFixed(2) + "×" : "—"} />
              <Kpi label="Default rate" value={fmtPct(rlz.default_rate_simple_cohort)} />
            </div>
          </Section>

          <Section title="Cost of Capital" category={coc?.status === "not_configured" ? "not_configured" : (coc?.category || "forecast")}
                    subtitle={coc?.status === "not_configured"
                        ? "No Capital Structure configured — cost of capital is not computed. Finance V2 does not assume a default rate."
                        : `Under ${struct?.name} @ ${struct?.annual_cost_of_capital_pct}% annual`}>
            {coc?.status === "not_configured" ? (
              <div className="text-sm text-[#6B7280]">Configure a Capital Structure to compute cost of capital.</div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <Kpi label="Cost on realized cohort" value={fmtMoney0(coc.cost_on_realized_cohort)} testid="kpi-coc-realized" />
                <Kpi label="Cost on open book to date" value={fmtMoney0(coc.cost_on_open_book_to_date)} />
                <Kpi label="Cost portfolio-wide to date" value={fmtMoney0(coc.cost_portfolio_wide_to_date)} />
              </div>
            )}
          </Section>

          <Section title="Net Portfolio Contribution" category={con?.status === "not_configured" ? "not_configured" : (con?.category === "realized" ? "realized" : "mixed")}>
            {con?.status === "not_configured" ? (
              <div className="text-sm text-[#6B7280]" data-testid="contribution-not-configured">
                Net Portfolio Contribution requires a configured Capital Structure. Configure one to see contribution, margin, and net return on deployed capital.
              </div>
            ) : (
              <>
                {mode === "actual" && (counts.simple_with_unassigned_coc > 0) && (
                  <div className="rounded-md bg-[#FFFBEB] border border-[#FCD34D] px-3 py-2 mb-3 text-[11px] text-[#78350F]"
                       data-testid="actual-mode-unassigned-notice">
                    {counts.simple_with_unassigned_coc} realized loan(s) have no capital-source snapshot and are excluded from the Cost of Capital math under Actual mode. Attach snapshots on the loan page to include them.
                  </div>
                )}
                <div className="grid grid-cols-3 gap-3">
                  <Kpi label="Net portfolio contribution" value={fmtMoney0(con.net_portfolio_contribution)} accent testid="kpi-net-contribution" />
                  <Kpi label="Contribution margin %" value={fmtPct(con.contribution_margin_pct)}
                       sub="Net Contribution ÷ Realized Funding Revenue" testid="kpi-contribution-margin" />
                  <Kpi label="Net return on deployed capital" value={fmtPct(con.net_return_on_deployed_capital)} />
                </div>
                {con.contribution_as_pct_of_collections != null && (
                  <div className="text-[11px] text-[#6B7280] mt-3">
                    Backward-compat reference: Net Contribution ÷ Total Collections = {fmtPct(con.contribution_as_pct_of_collections)} (not shown as margin — collections include returned principal).
                  </div>
                )}
                {/* Portfolio-level waterfall / bridge */}
                {Array.isArray(data?.bridge) && data.bridge.length > 0 && (
                  <div className="mt-5" data-testid="contribution-bridge">
                    <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">Contribution bridge (realized cohort)</div>
                    <div className="rounded-md border border-[#E5E7EB] bg-white overflow-hidden">
                      {data.bridge.map((step, i) => {
                        const isTotal = step.sign === "=";
                        return (
                          <div key={i}
                               className={`flex items-center justify-between px-4 py-2 border-b border-[#F3F4F6] last:border-b-0 ${isTotal ? "bg-[#F8FAFC] font-medium" : ""}`}
                               data-testid={`bridge-${step.step}`}>
                            <div className="text-sm text-[#0A2540] flex items-center gap-2">
                              <span className="inline-block w-5 text-center text-[#6B7280]">{step.sign}</span>
                              <span>{step.label}</span>
                              {step.category && <CategoryChip cat={step.category} />}
                            </div>
                            <div className={`text-sm tabular-nums ${isTotal ? "text-[#0A2540] font-semibold" : "text-[#374151]"}`}>
                              {fmtMoney0(step.amount)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-[11px] text-[#6B7280] mt-2">
                      Interest-vs-fee composition of Realized Funding Revenue is not surfaced because historical records do not uniformly preserve the split. It will be reported prospectively.
                    </div>
                  </div>
                )}
                <div className="text-[11px] text-[#4B5563] mt-4 space-y-1">
                  {(con.notes || []).map((n, i) => <div key={i}>· {n}</div>)}
                </div>
              </>
            )}
          </Section>
        </>
      )}
    </div>
  );
}
